import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePlatformSettings } from "@/hooks/usePlatformSettings";

/**
 * Global Launchpad status. While `live` is false the whole Launchpad
 * (public /ico page, in-app token sale, web3 launchpad) renders a
 * Bybit-style "Coming Soon" surface with every action locked.
 */
export function useLaunchpadStatus() {
  const { data, isLoading } = useQuery({
    queryKey: ["platform_settings", "launchpad"],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("get_public_platform_settings");
      if (error) throw error;
      return data as any;
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });

  const live = (data?.launchpad_live as boolean) ?? false;
  return { live, locked: !live, loading: isLoading, settingsId: data?.id as string | undefined };
}

export function useSetLaunchpadLive() {
  const qc = useQueryClient();
  const { settingsId } = usePlatformSettings();

  return useMutation({
    mutationFn: async (live: boolean) => {
      if (!settingsId) throw new Error("Platform settings not initialised");
      const { error } = await supabase
        .from("platform_settings" as any)
        .update({ launchpad_live: live } as any)
        .eq("id", settingsId);
      if (error) throw error;
      return live;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["platform_settings"] });
    },
  });
}

export function useLaunchpadNotify() {
  return useMutation({
    mutationFn: async ({ email, source }: { email: string; source: string }) => {
      const normalized = email.trim().toLowerCase();
      // The edge function records the signup and sends the confirmation email.
      const { data, error } = await supabase.functions.invoke("waitlist-confirm", {
        body: { email: normalized, source },
      });
      if (!error && !(data as any)?.error) return true;

      // Fall back to a direct insert so a mail outage never blocks the signup.
      const { data: auth } = await supabase.auth.getUser();
      const { error: insertError } = await supabase
        .from("launchpad_notify_signups" as any)
        .insert({ email: normalized, source, user_id: auth?.user?.id ?? null } as any);
      // Duplicate signup is a success from the user's point of view.
      if (insertError && !`${insertError.message}`.toLowerCase().includes("duplicate")) throw insertError;
      return true;
    },
  });
}

export function useLaunchpadSignups() {
  return useQuery({
    queryKey: ["launchpad-notify-signups"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("launchpad_notify_signups" as any)
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });
}
